import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
  FaGift
} from 'react-icons/fa';
import { FaCreditCard, FaSearch } from 'react-icons/fa';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { logo1, logo2, logo3, logo4 } from '../Logo/Logo';
import { Link } from 'react-router-dom';
import { summaryApi } from '../../common'; // ADDED

const Footer = () => {
  const { colors } = useTheme();
  const currentYear = new Date().getFullYear();
  const [selectedLogo, setSelectedLogo] = useState(logo1);

  useEffect(() => {
    const fetchCurrentLogo = async () => {
      try {
        const response = await fetch(summaryApi.getLogo.url, {
          method: summaryApi.getLogo.method,
          
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.logo) {
            setSelectedLogo(data.logo.cloudinaryUrl);
          } else {
            // Fallback to default logo
            setSelectedLogo(logo1);
          }
        } else {
          // Fallback to default logo if API fails
          setSelectedLogo(logo1);
        }
      } catch (error) {
        console.error('Error fetching logo:', error);
        // Fallback to default logo
        setSelectedLogo(logo1);
      }
    };

    fetchCurrentLogo();
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    }
  };

  const logoVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 15
      }
    },
    hover: {
      scale: 1.1,
      rotate: 5,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10
      }
    }
  };

  const logoImageVariants = {
    hover: {
      scale: 1.1,
      rotate: -2,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 10
      }
    }
  };

  const socialIconVariants = {
    hover: {
      scale: 1.2,
      rotate: 360,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10
      }
    }
  };

  const linkVariants = {
    hover: {
      x: 5,
      color: colors.primary,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 20
      }
    }
  };

  const textHoverVariants = {
    hover: {
      scale: 1.02,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10
      }
    }
  };

  const gradientLineVariants = {
    hidden: { scaleX: 0 },
    visible: {
      scaleX: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  // Function to check if logo is a video
  const isVideoLogo = (logoUrl) => {
    return logoUrl && (logoUrl.endsWith('.mp4') || logoUrl.includes('/video/'));
  };

  return (
    <motion.footer 
      className="relative w-full shadow-lg transition-colors duration-300"
      style={{ backgroundColor: colors.bgSecondary }}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      variants={containerVariants}
    >
      {/* Top gradient line */}
      <motion.div 
        className="absolute top-0 left-0 right-0 h-1 w-full"
        style={{
          background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent}, ${colors.primary})`
        }}
        variants={gradientLineVariants}
      />
      
      <div className="container mx-auto px-4 py-8 md:py-12">
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start"
          variants={containerVariants}
        >
          {/* Brand and Logo Section */}
          <motion.div 
            className="md:col-span-3 flex flex-col items-center md:items-start space-y-4"
            variants={itemVariants}
          >
            {/* Logo Image */}
            <motion.div 
              className='flex justify-between items-center gap-3'
              variants={logoVariants}
              whileHover="hover"
            >
              <motion.div 
                className='h-20 w-20 rounded-lg overflow-hidden shadow-lg'
                style={{ backgroundColor: colors.bgTertiary }}
                variants={logoImageVariants}
                whileHover="hover"
              >
                {selectedLogo ? (
                  isVideoLogo(selectedLogo) ? (
                    <video
                      src={selectedLogo}
                      className="h-full w-full object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  ) : (
                    <img
                      src={selectedLogo}
                      alt="JosiePhones Logo"
                      className='h-full w-full object-cover'
                    />
                  )
                ) : (
                  <div className="h-full w-full bg-gray-200 rounded-lg flex items-center justify-center">
                    <span className="text-xs text-gray-500">Logo</span>
                  </div>
                )}
              </motion.div>
              <motion.span 
                className="text-xl font-bold"
                style={{ color: colors.text }}
                variants={textHoverVariants}
                whileHover="hover"
              >
                JosiePhones
              </motion.span>
            </motion.div>

            {/* Brand Text */}
            <motion.div 
              className="text-sm font-bold text-center md:text-left"
              variants={itemVariants}
            >
              <motion.p 
                className="bg-gradient-to-r bg-clip-text text-transparent mb-2"
                style={{
                  backgroundImage: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`
                }}
                variants={textHoverVariants}
                whileHover="hover"
              >
                Buy It Easily at JosiePhones!
              </motion.p>
              <motion.p 
                className="text-sm leading-relaxed"
                style={{ color: colors.textSecondary }}
              >
                Your trusted partner for premium technology products and exceptional shopping experiences.
              </motion.p>
            </motion.div>
          </motion.div>

          {/* Quick Links - Responsive Grid */}
          <motion.div 
            className="md:col-span-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center md:text-left text-sm"
            variants={containerVariants}
          >
            {[
              {
                title: "Company",
                links: [
                  { name: "About", to: "/about" },
                  { name: "Careers", to: "/careers" },
                  { name: "Contact", to: "/contact" },
                  { name: "News", to: "/news" }
                ]
              },
              {
                title: "Support",
                links: [
                  { name: "Help Center", to: "/help" },
                  { name: "FAQs", to: "/faqs" },
                  { name: "Terms", to: "/terms" },
                  { name: "Privacy", to: "/privacy" }
                ]
              },
              {
                title: "Shop",
                links: [
                  { name: "Products", to: "/landing" },
                  { name: "Categories", to: "/product-category" },
                  { name: "Deals", to: "/flashdeals" },
                  { name: "Refurbished", to: "/refurbished" },
                  { name: "Book a repair", to: "/repair-requests" }
                ]
              },
              {
                title: "Gift Cards",
                links: [
                  { name: "Buy Gift Card", to: "/purchase-giftcard", icon: <FaGift /> },
                  { name: "Check Balance", to: "/check-giftcard-balance", icon: <FaSearch /> },
                  { name: "My Gift Cards", to: "/my-giftcards", icon: <FaCreditCard /> },
                  { name: "Redeem", to: "/received-giftcards" }
                ]
              }
            ].map((section, index) => (
              <motion.div 
                key={index} 
                className="space-y-2"
                variants={itemVariants}
              >
                <motion.h3 
                  className="font-bold text-sm transition-colors duration-300 mb-3"
                  style={{ color: colors.primary }}
                  variants={textHoverVariants}
                  whileHover="hover"
                >
                  {section.title}
                </motion.h3>
                <ul className="space-y-2">
                  {section.links.map((link, linkIndex) => (
                    <motion.li key={linkIndex}>
                      <Link 
                        to={link.to}
                        className="text-xs transition-all duration-300 flex items-center gap-1"
                        style={{ 
                          color: colors.textSecondary,
                          textDecoration: 'none'
                        }}
                      >
                        {link.icon && <span style={{ color: '#10b981' }}>{link.icon}</span>}
                        <motion.span
                          variants={linkVariants}
                          whileHover="hover"
                        >
                          {link.name}
                        </motion.span>
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>

          {/* Social and Copyright Section */}
          <motion.div 
            className="md:col-span-3 flex flex-col items-center md:items-end space-y-4"
            variants={itemVariants}
          >
            {/* Social Media Icons */}
            <motion.div 
              className="flex gap-3"
              variants={containerVariants}
            >
              {[
                { Icon: FaFacebookF, link: "https://www.facebook.com", label: "Facebook" },
                { Icon: FaTwitter, link: "https://www.twitter.com", label: "Twitter" },
                { Icon: FaInstagram, link: "https://www.instagram.com", label: "Instagram" },
                { Icon: FaLinkedinIn, link: "https://www.linkedin.com", label: "LinkedIn" },
                { Icon: FaYoutube, link: "https://www.youtube.com", label: "YouTube" }
              ].map(({ Icon, link, label }, index) => (
                <motion.a
                  key={index}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="group p-2.5 rounded-full transition-all duration-300"
                  style={{
                    backgroundColor: colors.bgTertiary
                  }}
                  variants={socialIconVariants}
                  whileHover="hover"
                  whileTap={{ scale: 0.9 }}
                >
                  <Icon 
                    className="text-lg transition-colors duration-300"
                    style={{ color: colors.text }}
                  />
                </motion.a>
              ))}
            </motion.div>

            {/* Copyright */}
            <motion.div 
              className="text-center md:text-right space-y-1"
              variants={itemVariants}
            >
              <motion.p 
                className="text-xs transition-colors duration-300"
                style={{ color: colors.text }}
                variants={textHoverVariants}
                whileHover="hover"
              >
                © {currentYear} JosiePhones.
              </motion.p>
              <motion.p 
                className="text-xs transition-colors duration-300"
                style={{ color: colors.textSecondary }}
                variants={textHoverVariants}
                whileHover="hover"
              >
                All rights reserved.
              </motion.p>
            </motion.div>

            {/* Additional Links */}
            <motion.div 
              className="flex gap-4 mt-2"
              variants={containerVariants}
            >
              {[
                { name: 'Privacy', to: '/privacy' },
                { name: 'Terms', to: '/terms' },
                { name: 'Cookies', to: '/cookies' }
              ].map((link) => (
                <Link
                  key={link.name}
                  to={link.to}
                  className="text-xs transition-colors"
                  style={{ color: colors.textSecondary }}
                >
                  <motion.span
                    variants={linkVariants}
                    whileHover="hover"
                  >
                    {link.name}
                  </motion.span>
                </Link>
              ))}
            </motion.div>

            {/* Gift Card Promo Box */}
            <motion.div 
              className="mt-4 p-4 rounded-lg text-center max-w-xs"
              style={{
                backgroundColor: `${colors.primary}15`,
                border: `1px solid ${colors.primary}30`
              }}
              variants={itemVariants}
            >
              <FaGift 
                className="text-2xl mx-auto mb-2" 
                style={{ color: '#10b981' }} 
              />
              <p className="text-xs font-bold mb-1" style={{ color: colors.text }}>
                🎁 Send the Perfect Gift
              </p>
              <p className="text-xs mb-3" style={{ color: colors.textSecondary }}>
                Gift cards make shopping easy!
              </p>
              <Link
                to="/purchase-giftcard"
                className="inline-block px-4 py-2 rounded-lg text-xs font-bold text-white transition-all hover:scale-105"
                style={{ backgroundColor: '#10b981' }}
              >
                Buy Now
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom gradient line */}
      <motion.div 
        className="absolute bottom-0 left-0 right-0 h-1 w-full"
        style={{
          background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent}, ${colors.primary})`
        }}
        variants={gradientLineVariants}
      />
    </motion.footer>
  );
};

export default Footer;